import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { HistoryPendingChangesActionType } from 'constants/pendingChanges';
import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';
import { mergePendingChanges } from 'utils/pendingChanges';

import { openConfirmationDialogAction } from 'store/global/actions';

import { PropertyAndLoanTestIds } from '../../../../../../PropertyAndLoan.testIds';

import { getOtherExpenseConfig } from './OtherExpensesForm.config';
import { containerStyles, formControlsStyles } from './OtherExpensesForm.styles';

const defaultObject = {};

const OtherExpensesForm = ({
  data,
  isLoading,
  cancelForm,
  pendingChanges = defaultObject,
  saveForm,
}) => {
  const dispatch = useDispatch();

  const observableFieldsNames = Object.keys(data);

  const { initialFormValues, schema, fieldsConfig } = useMemo(() => {
    const initialConfig = getOtherExpenseConfig();

    return getConfigInfo({
      applicationData: data,
      config: initialConfig,
      isNestedForm: true,
    });
  }, [data]);

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    isFormChanged,
    validateField,
    validateForm,
    staticValidateField,
    formPendingChanges,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const { mergedPendingChanges, isPendingChangesHighlightDisabled } = useMemo(() => {
    const mergedPendingChanges = mergePendingChanges(pendingChanges, formPendingChanges);

    return {
      mergedPendingChanges,
      isPendingChangesHighlightDisabled: Object.values(mergedPendingChanges).some(
        ({ actionType }) => actionType === HistoryPendingChangesActionType.ADDED,
      ),
    };
  }, [formPendingChanges, pendingChanges]);

  const formGeneratorConfig = useMemo(() => {
    return getOtherExpenseConfig();
  }, []);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  useEffect(() => {
    const { schema } = getConfigInfo({ applicationData: form, config: formGeneratorConfig });

    setSchema(schema);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      saveForm({ updatedData: form, initialData: data });
    },
    [form, validateForm, saveForm, data],
  );

  const onCancelClick = useCallback(() => {
    if (!isFormChanged) {
      cancelForm(data);

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Cancel Changes',
        description: 'Are you sure you want to cancel changes?',
        onAccept: () => {
          cancelForm(data);
        },
      }),
    );
  }, [dispatch, isFormChanged, cancelForm, data]);

  return (
    <form
      noValidate
      data-ischanged={isFormChanged}
      data-testid={PropertyAndLoanTestIds.OTHER_EXPENSES_FORM}
      onSubmit={onSubmit}
    >
      <Box sx={containerStyles}>
        <FormGenerator
          isNestedForm
          config={formGeneratorConfig}
          disabledPendingChangesHighlight={isPendingChangesHighlightDisabled}
          form={form}
          formName={FormNames.OTHER_EXPENSES}
          handleChange={handleChange}
          isLoading={isLoading}
          numberOfColumns={4}
          pendingChanges={mergedPendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />

        <Box sx={formControlsStyles}>
          <Button
            data-testid={PropertyAndLoanTestIds.OTHER_EXPENSES_FORM_CANCEL_BUTTON}
            size="small"
            variant="line"
            onClick={onCancelClick}
          >
            Cancel
          </Button>
          <Button
            data-testid={PropertyAndLoanTestIds.OTHER_EXPENSES_FORM_SUBMIT_BUTTON}
            disabled={!isFormChanged}
            size="small"
            type="submit"
          >
            Add
          </Button>
        </Box>
      </Box>
    </form>
  );
};

OtherExpensesForm.propTypes = {
  data: PropTypes.object.isRequired,
  cancelForm: PropTypes.func.isRequired,
  saveForm: PropTypes.func.isRequired,
  pendingChanges: PropTypes.object,
  ...WithLoadingPropTypes,
};

export default memo(OtherExpensesForm);
